import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Product, ProductFilters, PaginatedProducts } from '@/types/database';

export const useProducts = (filters?: ProductFilters) => {
  const page = filters?.page || 1;
  const pageSize = filters?.pageSize || 25;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  return useQuery({
    queryKey: ['products', filters],
    queryFn: async (): Promise<PaginatedProducts> => {
      // Search for brands matching the search term
      let matchingBrandIds: string[] = [];
      if (filters?.search) {
        const { data: matchingBrands } = await supabase
          .from('brands')
          .select('id')
          .ilike('name', `%${filters.search}%`);
        
        if (matchingBrands && matchingBrands.length > 0) {
          matchingBrandIds = matchingBrands.map(b => b.id);
        }
      }

      // Build base query for counting
      let countQuery = supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('active', true);

      // Apply filters to count query
      if (filters?.category && filters.category !== 'all') {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', filters.category)
          .single();
        
        if (categoryData) {
          countQuery = countQuery.eq('category_id', categoryData.id);
        }
      }

      if (filters?.petType && filters.petType !== 'all') {
        if (filters.petType === 'other') {
          countQuery = countQuery.eq('pet_type', 'other');
        } else {
          countQuery = countQuery.or(`pet_type.eq.${filters.petType},pet_type.eq.both`);
        }
      }

      if (filters?.brand) {
        countQuery = countQuery.eq('brand_id', filters.brand);
      }

      if (filters?.search) {
        if (matchingBrandIds.length > 0) {
          const brandFilter = matchingBrandIds.map(id => `brand_id.eq.${id}`).join(',');
          countQuery = countQuery.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,${brandFilter}`);
        } else {
          countQuery = countQuery.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }
      }

      const { count } = await countQuery;

      // Build paginated query
      let query = supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          brand:brands(*),
          variations:product_variations(*)
        `)
        .eq('active', true);

      if (filters?.category && filters.category !== 'all') {
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', filters.category)
          .single();
        
        if (categoryData) {
          query = query.eq('category_id', categoryData.id);
        }
      }

      if (filters?.petType && filters.petType !== 'all') {
        if (filters.petType === 'other') {
          query = query.eq('pet_type', 'other');
        } else {
          query = query.or(`pet_type.eq.${filters.petType},pet_type.eq.both`);
        }
      }

      if (filters?.brand) {
        query = query.eq('brand_id', filters.brand);
      }

      if (filters?.search) {
        if (matchingBrandIds.length > 0) {
          const brandFilter = matchingBrandIds.map(id => `brand_id.eq.${id}`).join(',');
          query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,${brandFilter}`);
        } else {
          query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }
      }

      const { data, error } = await query
        .order('featured', { ascending: false })
        .order('name')
        .range(from, to);

      if (error) throw error;

      const totalCount = count || 0;
      const totalPages = Math.ceil(totalCount / pageSize);

      return {
        products: data as Product[],
        totalCount,
        totalPages,
        currentPage: page,
      };
    },
  });
};

export const useProduct = (slug: string) => {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          brand:brands(*),
          variations:product_variations(*)
        `)
        .eq('slug', slug)
        .eq('active', true)
        .single();

      if (error) throw error;
      return data as Product;
    },
    enabled: !!slug,
  });
};
